import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Chip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Pagination,
  CircularProgress
} from '@mui/material';
import {
  Shield,
  CheckCircle,
  XCircle,
  Eye,
  Download,
  FileText,
  Building,
  User
} from '@phosphor-icons/react';
import api from '../../utils/api';

function AdminVerification() {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [reviewDialog, setReviewDialog] = useState(false);
  const [rejectionDialog, setRejectionDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    fetchPendingVerifications();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [pagination.page]);

  const fetchPendingVerifications = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/business-verification/pending', {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          status: 'under_review'
        }
      });
      
      setBusinesses(response.data.businesses);
      setPagination(response.data.pagination);
    } catch (error) {
      setError('Failed to load pending verifications');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (businessId) => {
    try {
      setActionLoading(true);
      await api.put(`/api/business-verification/${businessId}/approve`, {
        notes: 'Approved by admin'
      });
      
      // Refresh the list
      await fetchPendingVerifications();
      setReviewDialog(false);
      setSelectedBusiness(null);
    } catch (error) {
      setError('Failed to approve verification');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }

    try {
      setActionLoading(true);
      await api.put(`/api/business-verification/${selectedBusiness._id}/reject`, {
        rejectionReason: rejectionReason.trim()
      });
      
      // Refresh the list
      await fetchPendingVerifications();
      setRejectionDialog(false);
      setSelectedBusiness(null);
      setRejectionReason('');
    } catch (error) {
      setError('Failed to reject verification');
    } finally {
      setActionLoading(false);
    }
  };

  const openReviewDialog = (business) => {
    setSelectedBusiness(business);
    setReviewDialog(true);
  };

  const openRejectionDialog = (business) => {
    setSelectedBusiness(business);
    setRejectionDialog(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'under_review':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <Shield size={32} weight="regular" color="#1976d2" />
        <Typography variant="h4" component="h1" sx={{ ml: 2, fontWeight: 'bold' }}>
          Supplier Verification Management
        </Typography>
      </Box>

      <Typography variant="body1" color="text.secondary" mb={4}>
        Review and approve supplier verification requests. Only verified suppliers appear in search results.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Verification Requests Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Pending Verification Requests ({pagination.total})
          </Typography>
          
          {businesses.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography variant="body1" color="text.secondary">
                No pending verification requests
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Business</TableCell>
                      <TableCell>Owner</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Submitted</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {businesses.map((business) => (
                      <TableRow key={business._id}>
                        <TableCell>
                          <Box>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {business.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {business.businessDetails?.businessType?.replace('_', ' ')}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <User size={16} />
                            <Box>
                              <Typography variant="body2">
                                {business.owner?.firstName} {business.owner?.lastName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {business.owner?.email}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={business.category} 
                            size="small" 
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(business.verification?.submittedAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={business.verification?.verificationStatus?.replace('_', ' ').toUpperCase()}
                            color={getStatusColor(business.verification?.verificationStatus)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Eye size={16} />}
                            onClick={() => openReviewDialog(business)}
                          >
                            Review
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <Box display="flex" justifyContent="center" mt={3}>
                  <Pagination
                    count={pagination.pages}
                    page={pagination.page}
                    onChange={(event, page) => setPagination(prev => ({ ...prev, page }))}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Review Dialog */}
      <Dialog
        open={reviewDialog}
        onClose={() => setReviewDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <Building size={24} />
            Review Business Verification
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedBusiness && (
            <Box>
              {/* Business Information */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Business Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">Business Name</Typography>
                      <Typography variant="body1">{selectedBusiness.name}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">Category</Typography>
                      <Typography variant="body1">{selectedBusiness.category}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">Business Type</Typography>
                      <Typography variant="body1">
                        {selectedBusiness.businessDetails?.businessType?.replace('_', ' ')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">Business Number</Typography>
                      <Typography variant="body1">
                        {selectedBusiness.businessDetails?.businessNumber || 'Not provided'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">VAT Number</Typography>
                      <Typography variant="body1">
                        {selectedBusiness.businessDetails?.vatNumber || 'Not provided'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">Trading Name</Typography>
                      <Typography variant="body1">
                        {selectedBusiness.businessDetails?.tradingName || 'Not provided'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Owner Information */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Owner Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">Name</Typography>
                      <Typography variant="body1">
                        {selectedBusiness.owner?.firstName} {selectedBusiness.owner?.lastName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">Email</Typography>
                      <Typography variant="body1">{selectedBusiness.owner?.email}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Verification Documents */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Verification Documents
                  </Typography>
                  <Grid container spacing={2}>
                    {selectedBusiness.verificationDocuments && Object.entries(selectedBusiness.verificationDocuments).map(([key, doc]) => {
                      if (key === 'additionalDocuments') return null;
                      // Only show documents that have actual content
                      if (!doc || !doc.url || !doc.filename || !doc.uploadedAt) return null;
                      
                      return (
                        <Grid item xs={12} md={6} key={key}>
                          <Card variant="outlined">
                            <CardContent>
                              <Box display="flex" alignItems="center" gap={2}>
                                <FileText size={24} color="#666" />
                                <Box flexGrow={1}>
                                  <Typography variant="subtitle2">
                                    {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {doc.filename}
                                  </Typography>
                                </Box>
                                <Box>
                                  <IconButton
                                    size="small"
                                    component="a"
                                    href={doc.url}
                                    target="_blank"
                                  >
                                    <Eye size={16} />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    component="a"
                                    href={doc.url}
                                    download
                                  >
                                    <Download size={16} />
                                  </IconButton>
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                </CardContent>
              </Card>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialog(false)}>
            Cancel
          </Button>
          <Button
            color="error"
            onClick={() => {
              setReviewDialog(false);
              openRejectionDialog(selectedBusiness);
            }}
            disabled={actionLoading}
          >
            Reject
          </Button>
          <Button
            color="success"
            variant="contained"
            onClick={() => handleApprove(selectedBusiness._id)}
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <CheckCircle size={16} />}
          >
            {actionLoading ? 'Approving...' : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog
        open={rejectionDialog}
        onClose={() => setRejectionDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <XCircle size={24} color="#f44336" />
            Reject Verification
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" mb={2}>
            Please provide a reason for rejecting this verification request:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Enter the reason for rejection..."
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectionDialog(false)}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleReject}
            disabled={actionLoading || !rejectionReason.trim()}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <XCircle size={16} />}
          >
            {actionLoading ? 'Rejecting...' : 'Reject Verification'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default AdminVerification;

